import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";

const PIPELINES = {
    client: ["Received", "Reviewing", "Shortlisted", "In Conversation", "Onboarded", "Declined"],
    doctor: ["Awaiting Payment", "Under Review", "Diagnosis Scheduled", "Prescription Sent", "Closed"],
    academy: ["Application Received", "Screening", "Test", "Interview", "Scholarship Offered", "Selected", "Rejected"],
    career: ["Applied", "Under Review", "Assignment", "Interview", "Final Decision"],
};

const LABELS = {
    client: "Client Application",
    doctor: "Dr. Aangan Theory Case",
    academy: "Academy Application",
    career: "Career Application",
};

function PipelineStrip({ type, current }) {
    const steps = PIPELINES[type] || [];
    const idx = steps.indexOf(current);
    return (
        <div className="space-y-3">
            {steps.map((s, i) => {
                const active = i === idx;
                const done = idx >= 0 && i < idx;
                return (
                    <div key={s} className="flex items-center gap-4">
                        <div
                            className={`w-6 h-6 grid place-items-center font-mono text-xs ${
                                done
                                    ? "bg-saffron text-black"
                                    : active
                                      ? "border is-active"
                                      : "border border-white/15 text-white/45"
                            }`}
                        >
                            {i + 1}
                        </div>
                        <div className={`text-sm ${active ? "text-white" : done ? "text-white/75" : "text-white/45"}`}>
                            {s}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function StatusPage() {
    const params = useParams();
    const [q, setQ] = useState(params.trackingId || "");
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const lookup = async (id) => {
        if (!id) return;
        setBusy(true);
        setData(null);
        try {
            const { data } = await api.get(`/status/${id}`);
            setData(data);
        } catch (err) {
            toast.error("Tracking ID not found");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (params.trackingId) lookup(params.trackingId);
    }, [params.trackingId]);

    return (
        <main className="min-h-screen" data-testid="status-page">
            <Navbar />
            <section className="pt-32 pb-16 lg:pt-44 lg:pb-12 border-b border-white/8">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5 text-saffron">Track</div>
                    <h1 className="font-display text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-8">
                        Status & <span className="text-saffron-brand">tracking</span>.
                    </h1>
                    <p className="text-white/65 text-lg max-w-xl mb-10">
                        Enter your tracking ID (e.g. <span className="font-mono text-white">AT-CL-1001</span>) to see
                        where things stand.
                    </p>
                    <div className="flex items-center gap-3 border-b border-white/15 focus-within:border-saffron transition-colors">
                        <Search size={16} className="text-white/45" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value.toUpperCase())}
                            onKeyDown={(e) => e.key === "Enter" && lookup(q)}
                            className="field-input border-none flex-1 font-mono tracking-widest"
                            placeholder="AT-XX-1001"
                            data-testid="status-input"
                        />
                        <button onClick={() => lookup(q)} disabled={busy} className="btn-primary text-xs" data-testid="status-lookup-btn">
                            {busy ? <Loader2 size={14} className="animate-spin" /> : "Look up"}
                        </button>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    {data && (
                        <div className="card-surface p-8 lg:p-12 grid grid-cols-1 md:grid-cols-12 gap-10" data-testid="status-result">
                            <div className="md:col-span-5">
                                <div className="eyebrow mb-2">{LABELS[data.type] || "Application"}</div>
                                <div className="font-mono text-2xl text-saffron tracking-widest">{data.tracking_id}</div>
                                <div className="hairline my-6" />
                                <dl className="space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <dt className="text-white/55">Status</dt>
                                        <dd className="text-white">{data.status}</dd>
                                    </div>
                                    {data.payment_status && (
                                        <div className="flex justify-between">
                                            <dt className="text-white/55">Payment</dt>
                                            <dd className={`${data.payment_status === "paid" ? "text-saffron" : "text-white/75"}`}>
                                                {data.payment_status}
                                            </dd>
                                        </div>
                                    )}
                                    {data.appointment_status && (
                                        <div className="flex justify-between">
                                            <dt className="text-white/55">Appointment</dt>
                                            <dd className="text-white">{data.appointment_status}</dd>
                                        </div>
                                    )}
                                    {data.diagnosis_status && (
                                        <div className="flex justify-between">
                                            <dt className="text-white/55">Diagnosis</dt>
                                            <dd className="text-white">{data.diagnosis_status}</dd>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <dt className="text-white/55">Submitted</dt>
                                        <dd className="text-white/75">{new Date(data.created_at).toLocaleString()}</dd>
                                    </div>
                                </dl>
                            </div>
                            <div className="md:col-span-7">
                                <div className="eyebrow mb-4">Pipeline</div>
                                <PipelineStrip type={data.type} current={data.status} />
                            </div>
                        </div>
                    )}
                    {!data && !busy && (
                        <p className="text-white/45 text-sm">Enter your tracking ID above to view its current status.</p>
                    )}
                </div>
            </section>

            <Footer />
        </main>
    );
}
